// =====================================================================
// ChoixClient.jsx — Commandes prêtes v2 (16-10-2026) — champ « Client »
// =====================================================================
// Composant réutilisé par la liste (CommandePrete.jsx), l'écran PDA
// (CommandePreteEditeur.jsx) et le chargement (CommandesPretesChargement.jsx).
// Propositions pendant la frappe (logique PURE : ./clients.js, testée) :
//   - sans accents ni casse, sous-chaîne, partie trouvée surlignée ;
//   - Entrée = proposition mise en avant (la 1re ; ↑ / ↓ pour changer) ;
//   - liste réduite à UN client égal à la saisie → choisi tout seul ;
//   - rien ne correspond → « Aucun client ne correspond » (jamais une liste vide muette).
// Ce qui est affiché est toujours ce qui est utilisé : retoucher le nom d'un client
// choisi retire le choix (onChoix(null)) ; en quittant le champ sans nouveau choix,
// le client en vigueur (`valeur`) revient dans le champ ; Entrée sur le nom du client en
// vigueur le garde (jamais un autre client qui contiendrait ce nom).
// Props : id, label, clients (null = en cours de chargement), erreurListe, valeur,
// onChoix(client | null), disabled, autoFocus, inputRef, aideAucun(texte) → message,
// suivant() → champ à focaliser après un choix (APRÈS le rendu : il peut venir d'être dégrisé).
// =====================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  propositions, segments, choixAuto, choixEntree, memeClient, normClient,
  MSG_AUCUN_CLIENT, MSG_TAPER_CLIENT, MSG_CLIENT_NON_CHOISI, MAX_PROPOSITIONS,
} from './clients.js';

export default function ChoixClient({
  id = 'client', label = 'Client', clients, erreurListe, valeur, onChoix, disabled = false,
  autoFocus = false, inputRef, aideAucun, suivant,
}) {
  const [texte, setTexte] = useState(valeur ? valeur.name : '');
  const [ouvert, setOuvert] = useState(false);
  const [actif, setActif] = useState(0);
  const [msg, setMsg] = useState('');
  const interne = useRef(null);
  const champ = inputRef || interne;
  // Un choix vient d'être fait : le parent déplace le focus (réf., camion…) AVANT que ce composant
  // ait reçu la nouvelle `valeur` — la sortie du champ qui suit ne doit ni remettre l'ancien client
  // ni afficher « client non choisi ».
  const vientDeChoisir = useRef(false);
  // Après un choix, le champ suivant (réf., camion…) reçoit le focus une fois le rendu fait.
  const [choixSeq, setChoixSeq] = useState(0);
  useEffect(() => {
    if (!choixSeq || !suivant) return;
    const el = suivant();
    if (el && !el.disabled) el.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choixSeq]);

  // Le client en vigueur (choisi ici, ou celui du serveur) s'affiche dans le champ. Retiré par le
  // parent (choix refusé par le serveur, sans client avant) : son nom quitte le champ — sauf si le
  // texte a été retapé (le choix a été retiré par la frappe : la frappe reste).
  const precedent = useRef(valeur);
  useEffect(() => {
    const avant = precedent.current;
    precedent.current = valeur;
    if (valeur) { setTexte(valeur.name); setMsg(''); } else if (avant && memeClient(avant, texte)) setTexte('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valeur?.id, valeur?.name]);

  const liste = useMemo(() => propositions(clients || [], texte), [clients, texte]);
  // Liste non reçue (serveur, réseau) : on le dit — jamais « vérifiez l'orthographe ».
  const indispo = erreurListe ? `Liste des clients indisponible (${erreurListe}) : rechargez la page` : null;
  const aucun = (t) => indispo || (aideAucun && aideAucun(t)) || MSG_AUCUN_CLIENT;

  const choisir = (c) => {
    vientDeChoisir.current = true;
    setTexte(c.name);
    setOuvert(false);
    setActif(0);
    setMsg('');
    setChoixSeq((n) => n + 1);
    onChoix(c);
  };

  const onChange = (e) => {
    const v = e.target.value;
    vientDeChoisir.current = false;
    setTexte(v);
    setOuvert(true);
    setActif(0);
    setMsg('');
    if (valeur && !memeClient(valeur, v)) onChoix(null);
    const auto = choixAuto(clients || [], v);
    if (auto) choisir(auto);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!ouvert) { setOuvert(true); return; }
      setActif((a) => Math.max(0, Math.min(a + 1, Math.min(liste.length, MAX_PROPOSITIONS) - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActif((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Escape') {
      if (ouvert) { e.preventDefault(); setOuvert(false); }
      if (valeur) setTexte(valeur.name);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (clients === null || clients === undefined) { setMsg('Liste des clients en cours de chargement…'); return; }
      if (!normClient(texte)) {
        if (valeur) choisir(valeur);
        else { setMsg(MSG_TAPER_CLIENT); setOuvert(true); }
        return;
      }
      // (Le texte nomme encore le client en vigueur → il est gardé : voir choixEntree.)
      const c = choixEntree(clients, texte, ouvert ? actif : 0, valeur);
      if (c) choisir(c);
      else { setMsg(aucun(texte)); e.target.select(); }
    }
  };

  const onFocus = (e) => {
    vientDeChoisir.current = false;
    e.target.select();
    if (!normClient(texte)) setOuvert(true);   // champ vide : tous les clients, à toucher
  };
  const onBlur = () => {
    setOuvert(false);
    if (vientDeChoisir.current) { vientDeChoisir.current = false; return; }
    if (valeur && !memeClient(valeur, texte)) { setTexte(valeur.name); setMsg(''); }
    else if (!valeur && normClient(texte) && clients) setMsg(liste.length ? MSG_CLIENT_NON_CHOISI : aucun(texte));
  };

  const montre = ouvert && !disabled;
  const visibles = liste.slice(0, MAX_PROPOSITIONS);
  const idListe = `${id}-liste`;
  return (
    <div className="cc">
      <style>{CSS}</style>
      <label htmlFor={id} className="cc-lbl">{label}</label>
      <input id={id} ref={champ} type="text" role="combobox" autoComplete="off" spellCheck={false} autoCapitalize="words"
        aria-expanded={montre} aria-controls={idListe} aria-autocomplete="list"
        aria-activedescendant={montre && visibles.length ? `${id}-opt-${actif}` : undefined}
        placeholder="Tapez le nom du client…" value={texte} disabled={disabled} autoFocus={autoFocus}
        className={'cc-inp' + (msg ? ' bad' : '') + (valeur && memeClient(valeur, texte) ? ' ok' : '')}
        onChange={onChange} onKeyDown={onKeyDown} onFocus={onFocus} onBlur={onBlur} />
      {montre && (
        <ul id={idListe} role="listbox" className="cc-liste" aria-label="Clients proposés">
          {!clients ? (
            <li className="cc-vide">Chargement des clients…</li>
          ) : !visibles.length ? (
            <li className="cc-vide" role="alert">{indispo || (normClient(texte) ? aucun(texte) : 'Aucun client proposé : à activer dans « Paramètres clients ».')}</li>
          ) : visibles.map((c, i) => (
            <li key={c.id} id={`${id}-opt-${i}`} role="option" aria-selected={i === actif}>
              {/* mousedown sans effet : le champ garde le focus, le clic choisit. */}
              <button type="button" tabIndex={-1} className={'cc-opt' + (i === actif ? ' actif' : '')}
                onMouseDown={(e) => e.preventDefault()} onClick={() => choisir(c)}>
                <span>{segments(c.name, texte).map((s, k) => (s.m ? <mark key={k}>{s.t}</mark> : <span key={k}>{s.t}</span>))}</span>
                {c.carrierCode ? <span className="cc-code">{c.carrierCode}</span> : null}
              </button>
            </li>
          ))}
          {clients && liste.length > MAX_PROPOSITIONS && (
            <li className="cc-vide">{`… et ${liste.length - MAX_PROPOSITIONS} autre(s) : tapez plus de lettres`}</li>
          )}
        </ul>
      )}
      {indispo && !msg && <span className="cc-msg err">{indispo}</span>}
      {msg && <span className="cc-msg err" role="alert">{msg}</span>}
    </div>
  );
}

const CSS = `
.cc{display:flex;flex-direction:column;gap:4px;min-width:0}
.cc .cc-lbl{font-size:.8rem;font-weight:700;color:#5b6b73;letter-spacing:.02em}
.cc .cc-inp{font:inherit;font-size:16px;color:#17232a;background:#fff;border:1px solid #cfd8dc;border-radius:8px;padding:8px 10px;width:100%;min-width:0;min-height:44px;box-sizing:border-box}
.cc .cc-inp.ok{border-color:#2a7034;background:#f2faf2}
.cc .cc-inp.bad{border-color:#a6342b;background:#f8dedb}
.cc .cc-inp:disabled{opacity:.6;cursor:not-allowed;background:#e9eef0}
.cc .cc-inp:focus-visible{outline:3px solid #0f6e74;outline-offset:2px}
.cc .cc-liste{list-style:none;margin:0;padding:0;border:1px solid #cfd8dc;border-radius:8px;background:#fff;overflow:hidden}
.cc .cc-liste li+li{border-top:1px solid #e9eef0}
.cc .cc-opt{display:flex;justify-content:space-between;align-items:center;gap:8px;width:100%;min-height:44px;padding:8px 10px;border:0;background:#fff;color:#17232a;font:inherit;font-size:16px;text-align:left;cursor:pointer}
.cc .cc-opt.actif{background:#d7ecec}
.cc .cc-opt mark{background:#fbecd3;color:inherit;border-radius:3px;padding:0 1px}
.cc .cc-code{font-size:.8rem;color:#5b6b73;font-family:"JetBrains Mono",Consolas,monospace}
.cc .cc-vide{padding:8px 10px;font-size:.9rem;color:#5b6b73}
.cc .cc-msg{font-size:.86rem;overflow-wrap:anywhere}
.cc .cc-msg.err{color:#a6342b}
`;
